import * as React from "react";

import {
  duplicatePackWithId,
  getUserIsLoggedIn,
  updatePackToImperial,
  updatePackToMetric,
} from "../utils";

import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Copy,
  Download,
  ListChecks,
  MoreVertical,
  Scale,
  Share2,
  Trash2,
} from "lucide-react";
import { HEADING_FONT } from "../constants/colors";
import { useMediaQuery } from "react-responsive";

const KebabMenu = ({
  handleDeletePack,
  isSharedPack,
  tableId,
  setMainAlertDetails,
  setMainAlertIsOpen,
  handleSelectNewPack,
  handleViewAsChecklist,
  setShareSharedPackIsOpen,
  handleSetPackToMetric,
  handleSetPackToImperial,
  handleExportToCSV,
  handleExportToCSVShared,
  showRowImages,
  handleToggleRowImages,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 898 });
  const theme = useTheme();
  const iconColors = {
    primary: theme.palette.primary.main,
    error: theme.palette.error.main,
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [deletePackIsOpen, setDeletePackIsOpen] = React.useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteSelected = () => {
    handleDeletePack();
    setDeletePackIsOpen(false);
  };

  const handleConvertPackToMetric = async () => {
    const packWasUpdated = await updatePackToMetric(tableId);
    if (packWasUpdated && packWasUpdated.success) {
      setMainAlertDetails({
        type: "success",
        message: "Updated Pack to Metric",
      });
      setMainAlertIsOpen(true);
      if (getUserIsLoggedIn()) {
        handleSelectNewPack(tableId);
      } else {
        window.location = `${window.location.origin}/packs`;
      }
    } else {
      setMainAlertDetails({
        type: "error",
        message: "Failed to update pack to metric",
      });
      setMainAlertIsOpen(true);
    }
  };

  const handleConvertPackToImperial = async () => {
    const packWasUpdated = await updatePackToImperial(tableId);
    if (packWasUpdated && packWasUpdated.success) {
      setMainAlertDetails({
        type: "success",
        message: "Updated Pack to Imperial",
      });
      setMainAlertIsOpen(true);
      if (getUserIsLoggedIn()) {
        handleSelectNewPack(tableId);
      } else {
        window.location = `${window.location.origin}/packs`;
      }
    } else {
      setMainAlertDetails({
        type: "error",
        message: "Failed to update pack to Imperial",
      });
      setMainAlertIsOpen(true);
    }
  };

  const handleDuplicatePack = async () => {
    const duplicatedPack = await duplicatePackWithId(tableId);
    if (!duplicatedPack.message) {
      setMainAlertDetails({
        type: "success",
        message: "Duplicated Pack",
      });
      setMainAlertIsOpen(true);
      handleSelectNewPack(duplicatedPack.tableId);
    } else {
      setMainAlertDetails({
        type: "error",
        message: "Failed to duplicate Pack",
      });
      setMainAlertIsOpen(true);
    }
    handleClose();
  };

  return (
    <>
      <Dialog
        open={deletePackIsOpen}
        onClose={() => {
          setDeletePackIsOpen(false);
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
          },
        }}
      >
        <DialogTitle>{"Delete this Pack?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Once you delete this pack, it will be gone forever. Are you sure you
            want to delete this pack?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Trash2 size={18} />}
            variant="contained"
            disableElevation
            color="error"
            sx={{
              borderRadius: "9px",
              fontFamily: HEADING_FONT,
              fontWeight: 600,
              textTransform: "none",
            }}
            onClick={handleDeleteSelected}
          >
            Delete
          </Button>
          <Button
            sx={{
              borderRadius: "9px",
              fontFamily: HEADING_FONT,
              fontWeight: 600,
              textTransform: "none",
            }}
            onClick={() => {
              setDeletePackIsOpen(false);
            }}
          >
            Keep Pack
          </Button>
        </DialogActions>
      </Dialog>
      <IconButton
        aria-haspopup="true"
        onClick={handleClick}
        sx={{
          width: "48px",
          height: "48px",
          marginLeft: isMobile ? "10px" : "5px",
        }}
      >
        <MoreVertical size={isMobile ? 36 : 24} />
      </IconButton>
      <Menu
        id="menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "button",
        }}
        PaperProps={{ sx: { borderRadius: "12px" } }}
        sx={{ minWidth: "250px" }}
      >
        {!isSharedPack && (
          <MenuItem
            onClick={() => {
              setDeletePackIsOpen(true);
              handleClose();
            }}
            disabled={!getUserIsLoggedIn()}
          >
            <ListItemIcon>
              <Trash2 size={18} color={iconColors.error} />
            </ListItemIcon>
            <ListItemText>Delete Pack</ListItemText>
          </MenuItem>
        )}
        {isSharedPack && (
          <MenuItem onClick={setShareSharedPackIsOpen}>
            <ListItemIcon>
              <Share2 size={18} color={iconColors.primary} />
            </ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDuplicatePack} disabled={!getUserIsLoggedIn()}>
          <ListItemIcon>
            <Copy size={18} color={iconColors.primary} />
          </ListItemIcon>
          <ListItemText>Copy to "My Packs"</ListItemText>
        </MenuItem>
        {Boolean(handleViewAsChecklist) && (
          <MenuItem onClick={handleViewAsChecklist}>
            <ListItemIcon>
              <ListChecks size={18} color={iconColors.primary} />
            </ListItemIcon>
            <ListItemText>View as Checklist</ListItemText>
          </MenuItem>
        )}
        {!isSharedPack && Boolean(handleToggleRowImages) && (
          <MenuItem onClick={handleToggleRowImages}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                size="small"
                checked={Boolean(showRowImages)}
                tabIndex={-1}
                disableRipple
                sx={{ padding: "0px", marginLeft: "-2px" }}
              />
            </ListItemIcon>
            <ListItemText>Show Images</ListItemText>
          </MenuItem>
        )}
        {!isSharedPack && (
          <MenuItem onClick={handleConvertPackToMetric}>
            <ListItemIcon>
              <Scale size={18} color={iconColors.primary} />
            </ListItemIcon>
            <ListItemText>Convert Pack to Metric</ListItemText>
          </MenuItem>
        )}
        {!isSharedPack && (
          <MenuItem onClick={handleConvertPackToImperial}>
            <ListItemIcon>
              <Scale size={18} color={iconColors.primary} />
            </ListItemIcon>
            <ListItemText>Convert Pack to Imperial</ListItemText>
          </MenuItem>
        )}
        {isSharedPack && (
          <MenuItem
            onClick={() => {
              handleSetPackToMetric();
              handleClose();
            }}
          >
            <ListItemIcon>
              <Scale size={18} color={iconColors.primary} />
            </ListItemIcon>
            <ListItemText>View Pack as Metric</ListItemText>
          </MenuItem>
        )}
        {isSharedPack && (
          <MenuItem
            onClick={() => {
              handleSetPackToImperial();
              handleClose();
            }}
          >
            <ListItemIcon>
              <Scale size={18} color={iconColors.primary} />
            </ListItemIcon>
            <ListItemText>View Pack as Imperial</ListItemText>
          </MenuItem>
        )}
        {!isSharedPack && (
          <MenuItem onClick={handleExportToCSV}>
            <ListItemIcon>
              <Download size={18} color={iconColors.primary} />
            </ListItemIcon>
            <ListItemText>Export to CSV</ListItemText>
          </MenuItem>
        )}
        {isSharedPack && (
          <MenuItem onClick={handleExportToCSVShared}>
            <ListItemIcon>
              <Download size={18} color={iconColors.primary} />
            </ListItemIcon>
            <ListItemText>Export to CSV</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default KebabMenu;
